<script lang="ts">
  import { TRIAL_LENGTHS } from '../../core/balance';
  import { sound } from '../lib/audio';
  import { fmt } from '../lib/format';
  import { game } from '../lib/game.svelte';
  import Icon from './Icon.svelte';

  const t = $derived(game.trial);
  const elapsed = $derived(t.length * 1000 - t.remainingMs);
  const rate = $derived(elapsed > 250 ? (t.taps / elapsed) * 1000 : 0);
  const best = $derived(game.s.trialBests[t.length]);

  $effect(() => {
    if (t.phase === 'done') sound.trialEnd(t.isBest);
  });
</script>

<section class="trial" data-phase={t.phase} aria-label="Speed trial">
  {#if t.phase === 'armed'}
    <div class="row">
      <div class="lengths" role="radiogroup" aria-label="Trial length">
        {#each TRIAL_LENGTHS as len (len)}
          <button
            role="radio"
            aria-checked={t.length === len}
            class:on={t.length === len}
            onclick={() => game.armTrial(len)}>{len}s</button
          >
        {/each}
      </div>
      <button class="x" onclick={() => game.closeTrial()} aria-label="Close speed trial">
        <Icon name="close" size={18} />
      </button>
    </div>
    <p class="hint">
      Tap the dial to start. Best <strong class="num">{fmt(best)}</strong> taps.
    </p>
  {:else if t.phase === 'running'}
    <div class="row">
      <span class="clock num">{(t.remainingMs / 1000).toFixed(1)}<small>s</small></span>
      <span class="taps num">{fmt(t.taps)} <small>taps</small></span>
      <span class="rate num">{rate.toFixed(1)} <small>/s</small></span>
    </div>
    <div class="bar">
      <span style:transform="scaleX({t.remainingMs / (t.length * 1000)})"></span>
    </div>
  {:else if t.phase === 'done'}
    <div class="row">
      <div class="result">
        <span class="eyebrow">{t.isBest ? 'New personal best' : `${t.length}s trial`}</span>
        <span class="score num" class:best={t.isBest}>{fmt(t.taps)} taps</span>
        <span class="hint num">{(t.taps / t.length).toFixed(1)} per second · best {fmt(best)}</span>
      </div>
      <div class="actions">
        <button class="btn btn-primary" onclick={() => game.armTrial(t.length)}>Again</button>
        <button class="btn" onclick={() => game.closeTrial()}>Done</button>
      </div>
    </div>
  {/if}
</section>

<style>
  .trial {
    width: 100%;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    animation: rise var(--t) var(--ease-out);
  }

  @keyframes rise {
    from {
      transform: translateY(6px);
      opacity: 0;
    }
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .lengths {
    display: inline-flex;
    padding: 3px;
    gap: 2px;
    border-radius: 999px;
    background: var(--bg-sunken);
  }
  .lengths button {
    min-width: 52px;
    min-height: 34px;
    border-radius: 999px;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-2);
    transition: background-color var(--t) var(--ease-out);
  }
  .lengths button.on {
    background: var(--accent);
    color: var(--on-accent);
  }

  .x {
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: var(--ink-3);
  }

  .hint {
    font-size: var(--text-sm);
    color: var(--ink-2);
  }

  .clock {
    font-size: var(--text-xl);
    font-weight: 700;
    color: var(--accent);
    min-width: 4.5ch;
  }
  .taps,
  .rate {
    font-size: var(--text-lg);
    font-weight: 600;
  }
  small {
    font-size: 0.6em;
    color: var(--ink-3);
    font-weight: 500;
  }

  .bar {
    height: 4px;
    border-radius: 2px;
    background: var(--bg-sunken);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--accent);
    transform-origin: left;
  }

  .result {
    display: flex;
    flex-direction: column;
  }
  .score {
    font-size: var(--text-xl);
    font-weight: 700;
  }
  .score.best {
    color: var(--accent);
  }
  .actions {
    display: flex;
    gap: var(--space-2);
  }
</style>
